import axios from "axios";
import { useCookies } from "vue3-cookies";

const { cookies } = useCookies();

// api is the Acada API, signed in as whoever holds the cookies.
export const api = axios.create({ baseURL: process.env.VUE_APP_BASE_URL });

api.interceptors.request.use((config) => {
  config.headers.Authorization = `Bearer ${cookies.get("token")}`;
  config.headers["x-api-key"] = cookies.get("api_key");
  return config;
});

// errorMessage is what to tell the user about a failed request.
export function errorMessage(e) {
  if (e && e.message && e.message.includes("Network")) {
    return "Please check your internet connection";
  }
  return (
    (e && e.response && e.response.data && e.response.data.message) ||
    "Something went wrong"
  );
}

// Signing in.
export const auth = {
  schoolByUrl: (url) => api.get("/school/fetch-by-url", { params: { url } }),
  schoolByCode: (code) => api.get(`/school/fetch-by-code/${encodeURIComponent(code)}`),
  login: (body) => api.post("/user/auth/login", body),
  // A challenged login is finished with the emailed code as `token`.
  login2fa: (body, token) => api.post("/user/auth/login/2fa", { ...body, token }),
  resendCode: (body) => api.post("/user/auth/otp/send", body),
  // A code from the main app, for someone already signed in there.
  redeemHandoff: (code) => api.post("/user/auth/handoff/redeem", { code }),
  logout: () => api.get("/user/auth/logout"),
};

// The signed-in user's own files.
export const files = {
  // upload stores a file privately, as the person's own.
  upload: (blob, name) => {
    const form = new FormData();
    form.append("file", blob, name);
    return api.post("/file/upload/single", form, { params: { visibility: "private" } });
  },
};

// The people in the signed-in user's school, to pick from.
export const people = {
  search: (search, limit = 8) =>
    api.get("/school/user/list", { params: { search, limit } }),
};

// The live endpoints (see FRONTEND_MIGRATION.md, "Live meetings").
export const live = {
  // list pages with a cursor: pass the previous page's next_cursor.
  list: (params) => api.get("/user/live/list", { params }),
  fetch: (id) => api.get(`/user/live/fetch/${id}`),
  create: (body) => api.post("/user/live/create", body),
  edit: (id, body) => api.put(`/user/live/edit/${id}`, body),
  destroy: (id) => api.delete(`/user/live/delete/${id}`),
  addUsers: (id, users) => api.post(`/user/live/users/add/${id}`, { users }),
  removeUsers: (id, users) =>
    api.post(`/user/live/users/remove/${id}`, { users }),
  hosts: (id, users, add) =>
    api.post(`/user/live/hosts/${add ? "add" : "remove"}/${id}`, { users }),
  byCode: (code) => api.get(`/user/live/fetch/code/${code}`),
  join: (code, anonymous) =>
    api.post(`/user/live/join/${code}`, { anonymous }),
  lobby: (id) => api.get(`/user/live/lobby/${id}`),
  admit: (id, identity) =>
    api.put(`/user/live/lobby/admit/${id}`, { identity }),
  deny: (id, identity) => api.put(`/user/live/lobby/deny/${id}`, { identity }),
  mute: (id, identity, source) =>
    api.put(`/user/live/mute/${id}`, { identity, source }),
  remove: (id, identity) => api.put(`/user/live/remove/${id}`, { identity }),
  stage: (id, identity, on) => api.put(`/user/live/stage/${id}`, { identity, on }),
  lock: (id, locked) => api.put(`/user/live/lock/${id}`, { locked }),
  end: (id) => api.put(`/user/live/end/${id}`),
  // Apps in a meeting.
  apps: () => api.get("/user/live/app/list"),
  openApps: (id) => api.get(`/user/live/app/open/${id}`),
  openApp: (id, appId) => api.post(`/user/live/app/open/${id}`, { app_id: appId }),
  closeApp: (id, appId) => api.post(`/user/live/app/close/${id}`, { app_id: appId }),
  appEditors: (id, appId, identities, editor) =>
    api.put(`/user/live/app/editors/${id}`, { app_id: appId, identities, editor }),
  appValue: (id, appId, key) => api.get(`/user/live/app/data/${id}/${appId}`, { params: { key } }),
  setAppValue: (id, appId, key, value) => api.put(`/user/live/app/data/${id}/${appId}`, { key, value }),
  record: (id, start) =>
    api.post(`/user/live/recording/${start ? "start" : "stop"}/${id}`),
};
